import { Dispatch } from "redux";
import { User, UserAttributes, Meta } from "typings";
import { Users } from "@pacerpro/api";
import { ROLES } from "@pacerpro/utils";

type DrawerState = "account" | "cases";

interface SelectedUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  office: string;
  role: string;
  pacerproEmail: string;
  pacerUser: string;
  pacerPassword: string;
  [key: string]: string | any;
}

type UsersPayload = {
  data: User[];
  meta?: Meta;
  links?: {
    first: string;
    prev?: string;
    next?: string;
    last: string;
  };
  included: any[];
  filteredTotal: number;
};
/////////////////////////////////////////////////
// USERS CONSTANTS
/////////////////////////////////////////////////

export const LOAD_USERS = "LOAD_USERS";
export const LOAD_MORE_USERS = "LOAD_MORE_USERS";
export const LOAD_SELECTED_USER = "LOAD_SELECTED_USER";
export const UNLOAD_SELECTED_USER = "UNLOAD_SELECTED_USER";
export const EDIT_USER_LIST = "EDIT_USER_LIST";
export const ADD_USER_TO_LIST = "ADD_USER_TO_LIST";
export const SET_USERS_DRAWER = "SET_USERS_DRAWER";
export const SET_USERS_LOADING = "SET_USERS_LOADING";
export const SET_USERS_NAV_LOADING = "SET_USERS_NAV_LOADING";
export const SET_USERS_PAGE = "SET_USERS_PAGE";
export const REMOVE_USER_FROM_LIST = "REMOVE_USER_FROM_LIST";

/////////////////////////////////////////////////
// USERS ACTIONS
/////////////////////////////////////////////////

export const loadUsers = ({
  data = [],
  included = [],
  meta,
  links,
  filteredTotal = 0,
}: UsersPayload) => ({
  type: LOAD_USERS,
  payload: { data, included, meta, links, filteredTotal },
});

export const loadSelectedUser = (payload?: SelectedUser) => ({
  type: LOAD_SELECTED_USER,
  payload,
});

export const unloadSelectedUser = () => ({
  type: UNLOAD_SELECTED_USER,
});

export const editUserList = (payload: User) => ({
  type: EDIT_USER_LIST,
  payload,
});

export const addUserToList = (payload: User) => ({
  type: ADD_USER_TO_LIST,
  payload,
});

export const setDrawer = (payload?: DrawerState) => ({
  type: SET_USERS_DRAWER,
  payload,
});

export const removeUserFromList = (payload: string) => ({
  type: REMOVE_USER_FROM_LIST,
  payload,
});

export const setUsersNavLoading = (payload: boolean) => ({
  type: SET_USERS_NAV_LOADING,
  payload,
});

export const setUsersLoading = (payload: boolean) => ({
  type: SET_USERS_LOADING,
  payload,
});

export const loadMoreUsers = (options: {
  sort?: string;
  filter?: { [key: string]: string | undefined };
  include?: string[];
  number?: number;
  size?: number;
}) => async (dispatch: Dispatch) => {
  try {
    dispatch(setUsersLoading(true));
    const usersResponse = await Users.getAll(options);
    dispatch({
      type: SET_USERS_PAGE,
      payload: options.number,
    });
    dispatch({
      type: LOAD_MORE_USERS,
      payload: usersResponse.data,
    });
    dispatch(setUsersLoading(false));
  } catch (error) {
    console.error(error);
  }
};

export const createUser = (
  attributes: UserAttributes,
  supportedBy?: User[],
) => async (dispatch: Dispatch) => {
  const createdUser = await Users.create({
    data: {
      type: "users",
      attributes,
    },
  });

  const newUser = createdUser.data.data;

  const supportRequest =
    newUser.attributes.role === ROLES.ATTORNEY
      ? Users.addAssignedStaff
      : Users.addSupportedAttorneys;

  if (supportedBy) {
    await Promise.all(
      supportedBy.map(support =>
        supportRequest({
          userId: newUser.id,
          supportId: support.id,
        })
          .then(user => user.data.data)
          // TODO: (at) add error messaging if this promise all fails
          .catch(() => undefined),
      ),
    );
  }

  const userResponse = await Users.getById(newUser.id, {
    include: ["supportedAttorneys", "assignedStaff"],
  });

  const user = userResponse.data.data;
  const includedSupport = userResponse.data.included || [];

  dispatch(
    loadSelectedUser({
      id: user.id,
      ...user.attributes,
      includedSupport,
    }),
  );
  dispatch(addUserToList(user));
};

export const editSelectedUser = (
  selectedUserId: string,
  attributes: UserAttributes,
  options?: {
    include?: string[];
  },
) => async (dispatch: Dispatch) => {
  const editedUserResponse = await Users.edit(
    selectedUserId,
    attributes,
    options,
  );
  dispatch(
    loadSelectedUser({
      id: editedUserResponse.data.data.id,
      includedSupport: editedUserResponse.data.included || [],
      ...editedUserResponse.data.data.attributes,
    }),
  );
  dispatch(editUserList(editedUserResponse.data.data));
};

export const editUser = (
  userId: string,
  attributes: UserAttributes,
  options?: { include?: string[] },
) => async (dispatch: Dispatch) => {
  try {
    const editedUserResponse = await Users.edit(userId, attributes, options);
    dispatch(editUserList(editedUserResponse.data.data));
  } catch (error) {
    console.error(error);
  }
};

export const deleteUser = (userId: string) => async (dispatch: Dispatch) => {
  await Users.removeUser({ userId });
  dispatch(removeUserFromList(userId));
};

export type UserAction =
  | {
      type: typeof LOAD_USERS;
      payload: UsersPayload;
    }
  | {
      type: typeof LOAD_MORE_USERS;
      payload: UsersPayload;
    }
  | {
      type: typeof LOAD_SELECTED_USER;
      payload?: SelectedUser;
    }
  | {
      type: typeof SET_USERS_DRAWER;
      payload?: DrawerState;
    }
  | {
      type: typeof SET_USERS_LOADING;
      payload: boolean;
    }
  | {
      type: typeof SET_USERS_NAV_LOADING;
      payload: boolean;
    }
  | {
      type: typeof SET_USERS_PAGE;
      payload: number;
    }
  | {
      type: typeof UNLOAD_SELECTED_USER;
    }
  | {
      type: typeof EDIT_USER_LIST;
      payload: User;
    }
  | {
      type: typeof ADD_USER_TO_LIST;
      payload: User;
    }
  | {
      type: typeof REMOVE_USER_FROM_LIST;
      payload: string;
    };

/////////////////////////////////////////////////
// USERS REDUCER
/////////////////////////////////////////////////

export interface State {
  navLoading: boolean;
  isLoading: boolean;
  page: number;
  filteredTotal: number;
  users: User[];
  user?: SelectedUser;
  links?: any;
  included?: any[];
  meta: Meta;
  drawer?: DrawerState;
}

const initialState: State = {
  navLoading: false,
  isLoading: false,
  page: 1,
  users: [],
  user: undefined,
  meta: {
    pageCount: 0,
    recordCount: 0,
  },
  filteredTotal: 0,
  links: {},
  included: [],
  drawer: undefined,
};

export default (state = initialState, action: UserAction) => {
  switch (action.type) {
    case LOAD_USERS: {
      return {
        ...state,
        isLoading: false,
        page: 1,
        users: action.payload.data,
        meta: action.payload.meta || state.meta,
        filteredTotal: action.payload.filteredTotal,
        links: action.payload.links,
        included: action.payload.included,
      };
    }
    case LOAD_MORE_USERS: {
      return {
        ...state,
        users: [...state.users, ...action.payload.data],
        included: action.payload.included,
      };
    }
    case LOAD_SELECTED_USER: {
      return { ...state, user: action.payload };
    }
    case UNLOAD_SELECTED_USER: {
      return { ...state, user: initialState.user };
    }
    case EDIT_USER_LIST: {
      return {
        ...state,
        users: state.users.map(user =>
          user.id === action.payload.id ? action.payload : user,
        ),
      };
    }
    case ADD_USER_TO_LIST: {
      return {
        ...state,
        users: [...state.users, action.payload],
        meta: {
          ...state.meta,
          recordCount: state.meta.recordCount + 1,
        },
      };
    }
    case SET_USERS_DRAWER: {
      return { ...state, drawer: action.payload };
    }
    case SET_USERS_LOADING: {
      return { ...state, isLoading: action.payload };
    }
    case SET_USERS_NAV_LOADING: {
      return { ...state, navLoading: action.payload };
    }
    case SET_USERS_PAGE: {
      return { ...state, page: action.payload };
    }
    case REMOVE_USER_FROM_LIST: {
      return {
        ...state,
        users: state.users.filter(user => user.id != action.payload),
      };
    }
    default:
      return state;
  }
};
